import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { 
  Search, User, Mail, Phone, Calendar, CreditCard, 
  ChevronRight, Loader2, Plus, MoreVertical, Download, Upload, Columns
} from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import { Link } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminPatients = () => {
  const { toast } = useToast();
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [stats, setStats] = useState({ totalPatients: 0, activeMembers: 0, newThisMonth: 0, totalRevenue: 0 });

  useEffect(() => {
    fetchPatients();
  }, []);

  const fetchPatients = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('authToken');
      const practiceId = localStorage.getItem('practiceId') || 'demo-practice-001';
      
      // Fetch patients
      const response = await fetch(
        `${BACKEND_URL}/api/patients?practiceId=${practiceId}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        if (data.length > 0) {
          setPatients(data);
          calculateStats(data);
        } else {
          // Use mock data if no patients in DB
          setPatients(mockPatients);
          calculateStats(mockPatients);
        }
      } else {
        setPatients(mockPatients);
        calculateStats(mockPatients);
      }
    } catch (err) {
      console.error('Error fetching patients:', err);
      setPatients(mockPatients);
      calculateStats(mockPatients);
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (patientList) => {
    setStats({
      totalPatients: patientList.length,
      activeMembers: patientList.filter(p => p.membershipPlan).length,
      newThisMonth: patientList.filter(p => {
        const joinDate = new Date(p.joinedDate);
        const now = new Date();
        return joinDate.getMonth() === now.getMonth() && joinDate.getFullYear() === now.getFullYear();
      }).length,
      totalRevenue: patientList.reduce((sum, p) => sum + (p.totalSpent || 0), 0)
    });
  };

  const mockPatients = [
    {
      id: 'p1',
      name: 'Sarah Johnson',
      email: 'sarah.j@email.com',
      phone: '+1 (555) 123-4567',
      membershipPlan: 'Radiance Plus',
      creditBalance: 180,
      totalSpent: 2450,
      appointmentsCount: 12,
      lastVisit: '2025-01-15',
      joinedDate: '2024-06-10',
      status: 'active',
      tags: ['VIP', 'Member']
    },
    {
      id: 'p2',
      name: 'Michael Brown',
      email: 'mbrown@email.com',
      phone: '+1 (555) 987-6543',
      membershipPlan: 'Glow Starter',
      creditBalance: 45,
      totalSpent: 890,
      appointmentsCount: 5,
      lastVisit: '2025-01-20',
      joinedDate: '2024-09-15',
      status: 'active',
      tags: ['Member']
    },
    {
      id: 'p3',
      name: 'Jessica Lee',
      email: 'jlee@email.com',
      phone: '+1 (555) 456-7890',
      membershipPlan: null,
      creditBalance: 0,
      totalSpent: 350,
      appointmentsCount: 2,
      lastVisit: '2025-01-10',
      joinedDate: '2024-12-01',
      status: 'active',
      tags: ['New']
    },
    {
      id: 'p4',
      name: 'Emily Davis',
      email: 'emily.d@email.com',
      phone: '+1 (555) 321-0987',
      membershipPlan: 'Elite Beauty',
      creditBalance: 420,
      totalSpent: 5200,
      appointmentsCount: 24,
      lastVisit: '2025-01-22',
      joinedDate: '2023-03-20',
      status: 'active',
      tags: ['VIP', 'Member', 'Loyal']
    },
    {
      id: 'p5',
      name: 'David Wilson',
      email: 'dwilson@email.com',
      phone: '+1 (555) 654-3210',
      membershipPlan: null,
      creditBalance: 0,
      totalSpent: 150,
      appointmentsCount: 1,
      lastVisit: '2024-11-30',
      joinedDate: '2024-11-30',
      status: 'inactive',
      tags: []
    }
  ];

  const filteredPatients = patients.filter(patient =>
    patient.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    patient.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Export clients to CSV
  const exportToCSV = () => {
    const headers = ['Name', 'Email', 'Phone', 'Membership Plan', 'Credit Balance', 'Total Spent', 'Last Visit', 'Status'];
    const csvContent = [
      headers.join(','),
      ...filteredPatients.map(p => [
        `"${p.name}"`,
        p.email,
        p.phone,
        p.membershipPlan || 'None',
        p.creditBalance,
        p.totalSpent,
        p.lastVisit,
        p.status
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `clients_export_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);

    toast({
      title: 'Export Complete',
      description: `Exported ${filteredPatients.length} clients to CSV`
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-8 w-8 animate-spin text-rose-500" />
        <span className="ml-2">Loading patients...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-patients">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Client Profiles</h1>
          <p className="text-gray-600">Manage your patient database and memberships</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" asChild>
            <Link to="/admin/import">
              <Upload className="h-4 w-4 mr-2" />
              Import
            </Link>
          </Button>
          <Button variant="outline" size="sm" onClick={exportToCSV}>
            <Download className="h-4 w-4 mr-2" />
            CSV
          </Button>
          <Button data-testid="add-patient-btn">
            <Plus className="h-4 w-4 mr-2" />
            Add Client
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <User className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.totalPatients}</p>
                <p className="text-sm text-gray-600">Total Clients</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary/20 rounded-lg">
                <CreditCard className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.activeMembers}</p>
                <p className="text-sm text-gray-600">Active Members</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <Plus className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.newThisMonth}</p>
                <p className="text-sm text-gray-600">New This Month</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <CreditCard className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">${stats.totalRevenue.toLocaleString()}</p>
                <p className="text-sm text-gray-600">Total Revenue</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <Card>
        <CardContent className="pt-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Search clients by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="patient-search"
            />
          </div>
        </CardContent>
      </Card>

      {/* Patient List */}
      <div className="grid gap-4">
        {filteredPatients.map((patient) => (
          <Card 
            key={patient.id} 
            className="hover:shadow-md transition-shadow cursor-pointer"
            onClick={() => setSelectedPatient(patient)}
            data-testid={`patient-card-${patient.id}`}
          >
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-primary/20 rounded-full flex items-center justify-center">
                    <span className="text-lg font-semibold text-primary">
                      {patient.name.split(' ').map(n => n[0]).join('')}
                    </span>
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-gray-900">{patient.name}</h3>
                      {patient.tags.map((tag, i) => (
                        <Badge 
                          key={i} 
                          variant={tag === 'VIP' ? 'default' : 'secondary'}
                          className="text-xs"
                        >
                          {tag}
                        </Badge>
                      ))}
                    </div>
                    <div className="flex items-center gap-4 text-sm text-gray-600 mt-1">
                      <span className="flex items-center gap-1">
                        <Mail className="h-3 w-3" />
                        {patient.email}
                      </span>
                      <span className="flex items-center gap-1">
                        <Phone className="h-3 w-3" />
                        {patient.phone}
                      </span>
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center gap-6">
                  <div className="text-right hidden md:block">
                    <p className="text-sm text-gray-600">Membership</p>
                    <p className="font-medium">
                      {patient.membershipPlan || 'No membership'}
                    </p>
                  </div>
                  <div className="text-right hidden md:block">
                    <p className="text-sm text-gray-600">Credits</p>
                    <p className="font-medium">{patient.creditBalance}</p>
                  </div>
                  <div className="text-right hidden md:block">
                    <p className="text-sm text-gray-600">Total Spent</p>
                    <p className="font-medium">${patient.totalSpent.toLocaleString()}</p>
                  </div>
                  <div className="text-right hidden lg:block">
                    <p className="text-sm text-gray-600">Last Visit</p>
                    <p className="font-medium">
                      {new Date(patient.lastVisit).toLocaleDateString()}
                    </p>
                  </div>
                  <ChevronRight className="h-5 w-5 text-gray-400" />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {filteredPatients.length === 0 && (
        <Card>
          <CardContent className="py-12 text-center">
            <User className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No clients found matching your search.</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AdminPatients;
